/**
 * ProductCell Component
 *
 * Reusable component for displaying product information in tables.
 * Shows product thumbnail, title, variant title, and optional test data badge.
 * Test data badge is positioned at bottom-left of product thumbnail.
 */

import {
  InlineStack,
  BlockStack,
  Text,
  Thumbnail,
} from '@shopify/polaris';
import { PackageIcon } from '@shopify/polaris-icons';
import { TestDataBadge } from '../ui/TestDataBadge';

/**
 * Props for ProductCell component
 */
export interface ProductCellProps {
  title: string;
  imageUrl?: string | null;
  variantTitle?: string | null;
  /** Whether to show test data badge on thumbnail */
  showTestBadge?: boolean;
}

/**
 * Product display cell for consistent product rendering across tables
 */
export function ProductCell({
  title,
  imageUrl,
  variantTitle,
  showTestBadge = false,
}: ProductCellProps) {
  return (
    <InlineStack gap="300" blockAlign="center" wrap={false}>
      {/* Thumbnail wrapper for badge positioning */}
      <div className="product-cell-thumbnail">
        <Thumbnail
          source={imageUrl || PackageIcon}
          alt={title}
          size="small"
        />
        {showTestBadge && (
          <div className="product-cell-test-badge">
            <TestDataBadge />
          </div>
        )}
      </div>
      <BlockStack gap="050">
        <Text as="span" variant="bodyMd">
          {title}
        </Text>
        {variantTitle && (
          <Text as="span" variant="bodySm" tone="subdued">
            {variantTitle}
          </Text>
        )}
      </BlockStack>
    </InlineStack>
  );
}
